import type { Entity, Path, Boundary } from '@/types/diagram';
import { throwApiError } from '@/lib/errors';
import type { RatingSeverity } from '@/lib/owaspRating';

const BACKEND_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

/* ── Backend response shapes ─────────────────────────────────────────── */

export interface VulnerabilityOut {
  id: number;
  project_id: number;
  external_id?: string | null;
  name: string;
  vulnerability?: string | null;
  scenario?: string | null;
  countermeasure?: string | null;
  reason?: string | null;
  rating_vector_string?: string | null;
  /** OWASP severity computed by the backend from the vector and threat type. */
  rating_severity?: RatingSeverity;
  asrg_ids?: string | null;
  source: string;
  status: string;
  status_note?: string | null;
  threatcat_threat_id?: number | null;
  threat_type?: string | null;
  diagram_id?: number | null;
  created_by: string;
  tc_synced?: boolean | null;
}

export interface QuestionOut {
  id: number;
  project_id: number;
  question_number: number;
  external_id?: string | null;
  security_domain: string;
  asrg_ids?: string | null;
  question: string;
  answer?: string | null;
  manual_status?: string | null;
  user_context?: string | null;
  notes?: string | null;
  source: string;
  created_by: string;
  vulnerabilities: VulnerabilityOut[];
}

export interface QuestionsListResponse {
  questions: QuestionOut[];
  permission?: string | null;
}

export interface QuickScanFinding {
  question_id: number;
  is_vulnerable: boolean;
  needs_clarification: boolean;
  vulnerability_title?: string | null;
  reason?: string | null;
  rating_vector_string?: string | null;
  asrg_ids: string[];
}

export interface QuickScanResponse {
  findings: QuickScanFinding[];
}

/* ── Service ─────────────────────────────────────────────────────────── */

export const questionsService = {
  /** Fetch all questions for a project (with embedded findings + permission). */
  getQuestions: async (projectId: number): Promise<QuestionsListResponse> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) await throwApiError(response, 'Failed to load questions');
    return response.json();
  },

  /** Create a manual question. */
  createQuestion: async (
    projectId: number,
    body: { security_domain: string; asrg_ids?: string; question: string; answer?: string; notes?: string },
  ): Promise<QuestionOut> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) await throwApiError(response, 'Failed to create question');
    return response.json();
  },

  /** Update a question (answer, notes, manual_status, etc.). */
  updateQuestion: async (
    projectId: number,
    questionId: number,
    body: Partial<{
      answer: string;
      manual_status: string | null;
      user_context: string | null;
      notes: string | null;
      security_domain: string;
      question: string;
      asrg_ids: string;
    }>,
  ): Promise<QuestionOut> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/${questionId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) await throwApiError(response, 'Failed to update question');
    return response.json();
  },

  /** Delete a question. */
  deleteQuestion: async (projectId: number, questionId: number): Promise<void> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/${questionId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) await throwApiError(response, 'Failed to delete question');
  },

  /** AI-generate questions (replaces all existing). */
  generate: async (
    projectId: number,
    body: {
      entities: Entity[];
      paths: Path[];
      boundaries: Boundary[];
      confidentiality?: string;
      integrity?: string;
      availability?: string;
      internet_facing?: boolean;
      isa_context?: string;
    },
  ): Promise<QuestionsListResponse> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/generate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) await throwApiError(response, 'Question generation failed');
    return response.json();
  },

  /** Quick scan (filtered server-side, persists manual_status and creates vulnerability records). */
  quickScan: async (
    projectId: number,
    body: { entities: Entity[]; paths: Path[]; boundaries: Boundary[] },
  ): Promise<QuickScanResponse> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/quick-scan`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) await throwApiError(response, 'Quick scan failed');
    return response.json();
  },

  /** Full analysis on a single question → persists vulnerability. */
  analyze: async (
    projectId: number,
    questionId: number,
    body: { entities: Entity[]; paths: Path[]; boundaries: Boundary[] },
  ): Promise<QuestionOut> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/${questionId}/analyze`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) await throwApiError(response, 'Vulnerability analysis failed');
    return response.json();
  },

  /** Mark a question as incorrect and run AI analysis. */
  markIncorrect: async (
    projectId: number,
    questionId: number,
    userContext: string,
  ): Promise<QuestionOut> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/${questionId}/mark-incorrect`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_context: userContext }),
    });
    if (!response.ok) await throwApiError(response, 'Mark incorrect failed');
    return response.json();
  },

  /** Import XLSX and smart-merge answers into existing questions. */
  importXlsx: async (projectId: number, file: File): Promise<QuestionsListResponse> => {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/import-xlsx`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!response.ok) await throwApiError(response, 'XLSX import failed');
    return response.json();
  },

  /** Download unified questionnaire (Questions + Findings tabs) as XLSX file. */
  exportQuestionnaireXlsx: async (projectId: number): Promise<void> => {
    const response = await fetch(`${BACKEND_BASE}/api/questions/${projectId}/export-questionnaire-xlsx`, {
      credentials: 'include',
    });
    if (!response.ok) await throwApiError(response, 'Failed to export questionnaire');
    const blob = await response.blob();
    // Extract filename from Content-Disposition header, fallback to default
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename=(.+)/);
    const filename = match ? match[1] : `questionnaire_${projectId}.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },

  /** Link a question to an existing vulnerability. */
  linkVulnerability: async (
    projectId: number,
    questionId: number,
    vulnerabilityId: number,
  ): Promise<QuestionOut> => {
    const response = await fetch(
      `${BACKEND_BASE}/api/questions/${projectId}/${questionId}/link-vulnerability`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vulnerability_id: vulnerabilityId }),
      },
    );
    if (!response.ok) await throwApiError(response, 'Failed to link vulnerability');
    return response.json();
  },

  /** Unlink a vulnerability from a question. */
  unlinkVulnerability: async (
    projectId: number,
    questionId: number,
    vulnerabilityId: number,
  ): Promise<QuestionOut> => {
    const response = await fetch(
      `${BACKEND_BASE}/api/questions/${projectId}/${questionId}/unlink-vulnerability/${vulnerabilityId}`,
      {
        method: 'DELETE',
        credentials: 'include',
      },
    );
    if (!response.ok) await throwApiError(response, 'Failed to unlink vulnerability');
    return response.json();
  },
};
